/**
 * MenuCalendarDropdown
 *
 * Compact trigger button + popover calendar used in the authenticated header.
 * - Shows the selected date as a single button when collapsed.
 * - Click/Enter/Space opens a popover with a Sun-Sat week strip (current week
 *   plus the following week) and the menu details for the selected day.
 * - Keyboard navigation: arrows move date (Left/Right by day, Up/Down by week),
 *   Enter/Space toggles open, Escape closes and returns focus to the trigger.
 * - Brand color: #10348e (Tailwind `hmh-blue`).
 *
 * Data: uses `useMenuCalendar(2)` from ../../hooks/useMenu. No backend changes.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  CalendarDay,
  CalendarWeek,
  DailySpecial,
  useMenuCalendar,
} from '../../hooks/useMenu';

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseLocalDate(dateStr: string): Date {
  // Treat the YYYY-MM-DD string as a local date by anchoring at noon.
  return new Date(dateStr + 'T12:00:00');
}

function formatLong(dateStr: string): string {
  return parseLocalDate(dateStr).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatShort(dateStr: string): string {
  return parseLocalDate(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function getSpecials(day: CalendarDay): DailySpecial[] {
  if (day.daily_specials && day.daily_specials.length > 0) {
    return day.daily_specials;
  }
  if (day.daily_special) {
    return [day.daily_special];
  }
  return [];
}

function specialBadgeLabel(mode: DailySpecial['replacement_mode']): string {
  switch (mode) {
    case 'replace_lunch':
      return 'Replaces lunch';
    case 'replace_special':
      return 'Replaces special';
    case 'add':
    default:
      return 'Special';
  }
}

export function MenuCalendarDropdown() {
  const { calendar, loading, error } = useMenuCalendar(2);

  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  // Flatten the calendar into a single ordered list of days (for nav + lookup).
  const allDays = useMemo<CalendarDay[]>(() => {
    if (!calendar) return [];
    return calendar.weeks.flatMap((w: CalendarWeek) => w.days);
  }, [calendar]);

  // Default the selection to today (or, if today isn't in range, the first day).
  useEffect(() => {
    if (selectedDate || allDays.length === 0) return;
    const today = allDays.find((d) => d.is_today);
    setSelectedDate(today ? today.date : allDays[0].date);
  }, [allDays, selectedDate]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // When the popover opens, move focus into it for keyboard users.
  useEffect(() => {
    if (open && popoverRef.current) {
      popoverRef.current.focus();
    }
  }, [open]);

  const moveSelection = (deltaDays: number) => {
    if (!selectedDate || allDays.length === 0) return;
    const idx = allDays.findIndex((d) => d.date === selectedDate);
    if (idx === -1) return;
    const next = Math.min(Math.max(idx + deltaDays, 0), allDays.length - 1);
    setSelectedDate(allDays[next].date);
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        moveSelection(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        moveSelection(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveSelection(-7);
        break;
      case 'ArrowDown':
        e.preventDefault();
        moveSelection(7);
        break;
      default:
        break;
    }
  };

  const selectedDay = useMemo<CalendarDay | null>(() => {
    if (!selectedDate) return null;
    return allDays.find((d) => d.date === selectedDate) ?? null;
  }, [allDays, selectedDate]);

  // Trigger button label.
  const triggerLabel = (() => {
    if (loading) return 'Loading menu…';
    if (error) return 'Menu calendar';
    if (!selectedDay) return 'Menu calendar';
    if (selectedDay.is_today) return `Today — ${formatShort(selectedDay.date)}`;
    return formatShort(selectedDay.date);
  })();

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        aria-label={`Menu calendar — ${selectedDay ? formatLong(selectedDay.date) : 'select a date'}`}
        className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-[#10348e] shadow-sm transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#10348e]"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="hidden sm:inline">{triggerLabel}</span>
        <span className="sm:hidden">Menu</span>
        <svg
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div
          id={popoverId}
          ref={popoverRef}
          role="dialog"
          aria-modal="false"
          aria-label="Menu calendar"
          tabIndex={-1}
          onKeyDown={handlePopoverKeyDown}
          className="absolute right-0 z-50 mt-2 w-[22rem] max-w-[calc(100vw-1.5rem)] rounded-xl border border-gray-200 bg-white text-gray-900 shadow-2xl focus:outline-none sm:w-[26rem]"
        >
          {loading && (
            <div className="p-6 text-center text-sm text-gray-500">
              Loading menu calendar…
            </div>
          )}

          {error && !loading && (
            <div className="p-6 text-center text-sm text-red-600">
              Failed to load menu calendar.
            </div>
          )}

          {!loading && !error && calendar && (
            <>
              {/* Week strip(s) */}
              <div className="border-b border-gray-100 bg-gray-50 p-3 sm:p-4">
                <div className="mb-2 flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold text-gray-900">
                    Pick a date
                  </h2>
                  <p
                    id={`${popoverId}-hint`}
                    className="text-xs text-gray-500"
                  >
                    Use arrow keys
                  </p>
                </div>

                {/* Sun..Sat header row */}
                <div className="mb-1 grid grid-cols-7 gap-1">
                  {DAY_ABBR.map((d) => (
                    <div
                      key={d}
                      className="text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400"
                    >
                      {d}
                    </div>
                  ))}
                </div>

                <div className="space-y-1">
                  {calendar.weeks.map((week) => (
                    <div
                      key={week.week_start}
                      className="grid grid-cols-7 gap-1"
                    >
                      {week.days.map((day) => {
                        const isSelected = day.date === selectedDate;
                        const hasSpecial = getSpecials(day).length > 0;
                        const hasMenu = Boolean(day.regular_lunch) || hasSpecial;
                        const dayNum = parseLocalDate(day.date).getDate();

                        const base =
                          'relative flex h-10 flex-col items-center justify-center rounded-md text-xs font-medium transition-colors focus:outline-none';
                        let stateClasses = '';
                        if (day.is_today && isSelected) {
                          stateClasses =
                            'bg-[#10348e] text-white ring-2 ring-[#10348e] ring-offset-1';
                        } else if (day.is_today) {
                          stateClasses =
                            'bg-[#10348e] text-white hover:bg-[#0d2b73]';
                        } else if (isSelected) {
                          stateClasses =
                            'bg-white text-[#10348e] ring-2 ring-[#10348e] ring-offset-1';
                        } else if (hasMenu) {
                          stateClasses =
                            'bg-white text-gray-800 hover:bg-gray-100 border border-gray-200';
                        } else {
                          stateClasses =
                            'bg-gray-100 text-gray-400 hover:bg-gray-200';
                        }

                        const ariaLabel = [
                          formatLong(day.date),
                          day.is_today ? 'today' : null,
                          isSelected ? 'selected' : null,
                          hasSpecial ? 'has special' : null,
                          hasMenu ? null : 'no menu set',
                        ]
                          .filter(Boolean)
                          .join(', ');

                        return (
                          <button
                            key={day.date}
                            type="button"
                            onClick={() => setSelectedDate(day.date)}
                            aria-label={ariaLabel}
                            aria-pressed={isSelected}
                            aria-current={day.is_today ? 'date' : undefined}
                            className={`${base} ${stateClasses}`}
                          >
                            <span className="leading-none">{dayNum}</span>
                            {hasSpecial && (
                              <span
                                className={`mt-0.5 inline-block h-1.5 w-1.5 rounded-full ${
                                  day.is_today
                                    ? 'bg-white'
                                    : 'bg-amber-500'
                                }`}
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Legend */}
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-sm bg-[#10348e]" />
                    Today
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-sm border-2 border-[#10348e] bg-white" />
                    Selected
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Special
                  </span>
                </div>
              </div>

              {/* Selected day details */}
              <div className="p-4">
                {selectedDay ? (
                  <SelectedDayDetails day={selectedDay} />
                ) : (
                  <p className="text-sm text-gray-500">
                    Select a date to see the menu.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface SelectedDayDetailsProps {
  day: CalendarDay;
}

function SelectedDayDetails({ day }: SelectedDayDetailsProps) {
  const specials = getSpecials(day);
  const replacesLunch = specials.some(
    (s) => s.replacement_mode === 'replace_lunch' || s.replaces_regular,
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">
          {formatLong(day.date)}
        </h3>
        {day.is_today && (
          <span className="rounded-full bg-[#10348e] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            Today
          </span>
        )}
      </div>

      <dl className="space-y-3 text-sm">
        {day.regular_lunch && !replacesLunch && (
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Regular lunch
            </dt>
            <dd className="mt-0.5 text-gray-900">{day.regular_lunch.name}</dd>
            {day.regular_lunch.description && (
              <dd className="mt-0.5 text-xs text-gray-500">
                {day.regular_lunch.description}
              </dd>
            )}
          </div>
        )}

        {specials.length > 0 && (
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {specials.length === 1 ? 'Daily special' : 'Daily specials'}
            </dt>
            <dd className="mt-0.5 space-y-2">
              {specials.map((s, i) => (
                <div
                  key={s.id ?? `${s.name}-${i}`}
                  className="rounded-md border border-amber-200 bg-amber-50 p-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-gray-900">{s.name}</p>
                    <span className="shrink-0 rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                      {specialBadgeLabel(s.replacement_mode)}
                    </span>
                  </div>
                  {s.description && (
                    <p className="mt-1 text-xs text-gray-600">
                      {s.description}
                    </p>
                  )}
                </div>
              ))}
            </dd>
          </div>
        )}

        {!day.regular_lunch && specials.length === 0 && (
          <div className="rounded-md bg-gray-50 p-3 text-center text-sm text-gray-500">
            No menu posted for this day yet.
          </div>
        )}
      </dl>
    </div>
  );
}

export default MenuCalendarDropdown;
